import classNames from 'classnames';
import { useRef, useState } from 'react';

export type StackSeries = { key: string; label: string; swatch: string; line: string };
export type StackColumn = { key: string; label: string; values: Record<string, number> };

const H = 160; // plot height, px
const GAP = 2; // surface gap between stacked segments, px

/** A clean tick step that splits `max` into about four intervals: 1, 2 or 5 times a power of ten. */
const niceStep = (max: number) => {
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
};

/**
 * Stacked columns for a few categorical series over time (dataviz): fixed colour slots,
 * a legend, a 2px surface gap between segments, each stack square at the baseline and
 * rounded 4px on top, columns 24px wide (12px on a phone, where every third date is
 * labelled). Hovering or focusing a column lists every series at that point, values
 * first, and on a touch screen a tap does (UI-1); the page puts the same numbers in a table.
 */
const StackedColumns = ({ columns, series, label }: { columns: StackColumn[]; series: StackSeries[]; label: string }) => {
  const [active, setActive] = useState<number | null>(null);
  // A finger leaves the column as soon as it lifts, so only a mouse shows and hides the tooltip by hovering; a tap toggles it.
  const pointer = useRef('mouse');
  const totals = columns.map((c) => series.reduce((n, s) => n + (c.values[s.key] ?? 0), 0));
  // Counts of leads: a step under 1 would draw ticks at half a lead.
  const step = Math.max(1, niceStep(Math.max(1, ...totals)));
  const top = Math.ceil(Math.max(1, ...totals) / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const px = (v: number) => (v / top) * H;

  return (
    <figure aria-label={label}>
      <ul aria-label="Legend" className="mb-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
        {series.map((s) => (
          <li key={s.key} className="inline-flex items-center gap-1.5">
            <span className={classNames('h-2.5 w-2.5 rounded-sm', s.swatch)} aria-hidden />
            {s.label}
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <div className="relative w-6 shrink-0 text-right text-[11px] tabular-nums text-fg-faint" style={{ height: H }} aria-hidden>
          {ticks.map((t) => (
            <span key={t} className="absolute right-0 translate-y-1/2" style={{ bottom: px(t) }}>
              {t}
            </span>
          ))}
        </div>
        <div className="relative flex-1" style={{ height: H }}>
          {ticks.map((t) => (
            <span key={t} className="absolute inset-x-0 h-px bg-line" style={{ bottom: px(t) }} aria-hidden />
          ))}
          <div className="absolute inset-0 flex">
            {columns.map((c, i) => {
              const shown = series.filter((s) => (c.values[s.key] ?? 0) > 0);
              return (
                <div
                  key={c.key}
                  tabIndex={0}
                  aria-label={`${c.label}: ${totals[i]} in total; ${shown.map((s) => `${s.label} ${c.values[s.key]}`).join(', ')}`}
                  onPointerDown={(e) => (pointer.current = e.pointerType)}
                  onPointerEnter={(e) => e.pointerType === 'mouse' && setActive(i)}
                  onPointerLeave={(e) => e.pointerType === 'mouse' && setActive(null)}
                  onClick={() => pointer.current !== 'mouse' && setActive((a) => (a === i ? null : i))}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                  className="relative flex h-full flex-1 items-end justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
                >
                  <div className={classNames('flex w-3 flex-col-reverse transition-opacity md:w-6', active === i && 'opacity-80')} style={{ gap: GAP }}>
                    {shown.map((s, si) => (
                      <div
                        key={s.key}
                        className={classNames(s.swatch, si === shown.length - 1 && 'rounded-t')}
                        // Every segment above the first gives up the gap below it, so the stack stays to scale.
                        style={{ height: Math.max(1, px(c.values[s.key]) - (si > 0 ? GAP : 0)) }}
                      />
                    ))}
                  </div>
                  {active === i && (
                    <div
                      role="tooltip"
                      className={classNames(
                        'pointer-events-none absolute bottom-full z-10 mb-2 whitespace-nowrap rounded-md bg-surface-raised px-2.5 py-2 text-xs shadow-e4 md:left-1/2 md:right-auto md:-translate-x-1/2',
                        // On a phone the tooltip stays on screen: it opens away from the nearer edge.
                        i < columns.length / 3 ? 'left-0' : i >= (columns.length * 2) / 3 ? 'right-0' : 'left-1/2 -translate-x-1/2',
                      )}
                    >
                      <p className="mb-1 text-fg-muted">{c.label}</p>
                      {series.map((s) => (
                        <p key={s.key} className="flex items-center gap-2">
                          <span className={classNames('h-0.5 w-3 rounded-full', s.line)} aria-hidden />
                          <span className="w-5 text-right font-semibold tabular-nums text-fg-strong">{c.values[s.key] ?? 0}</span>
                          <span className="text-fg-muted">{s.label}</span>
                        </p>
                      ))}
                      <p className="mt-1 flex items-center gap-2 border-t border-line pt-1">
                        <span className="w-3" aria-hidden />
                        <span className="w-5 text-right font-semibold tabular-nums text-fg-strong">{totals[i]}</span>
                        <span className="text-fg-muted">Total</span>
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-2 flex gap-2 text-[11px] text-fg-faint" aria-hidden>
        <span className="w-6 shrink-0" />
        <div className="flex flex-1">
          {columns.map((c, i) => (
            <span
              key={c.key}
              // On a phone only every third date is labelled, counting back from the latest, so the labels never overlap.
              className={classNames('w-0 min-w-0 flex-1 overflow-visible whitespace-nowrap text-center', (columns.length - 1 - i) % 3 !== 0 && 'invisible md:visible')}
            >
              {c.label}
            </span>
          ))}
        </div>
      </div>
    </figure>
  );
};

export default StackedColumns;
